/**
 * ContentRenderer Component
 * Renders lesson content (text, video, reader notes, PDF documents, images).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Stack,
  Typography,
  Paper,
  Alert,
  Card,
  CardContent,
  Chip,
  TextField,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import axios from 'axios';
import KindleReaderContent from './KindleReaderContent';
import CodingChallengeLauncher from './CodingChallengeLauncher';
import { useFullscreenContent } from './FullscreenContentContext';
import { FullscreenFillContainer } from './ContentFullscreenFrame';
import AndroidOnlyDocumentNotice, { PdfAppOnlyAlert } from './AndroidOnlyDocumentNotice';
import AttachmentPreviewPanel from './AttachmentPreviewPanel';
import VerseLoopPlayer from './VerseLoopPlayer';
import { hasWebDocumentAccess } from '../../../utils/studentDevAccess';
import { getStudentApiHeaders } from '../../../utils/studentPlatform';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  }
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname.includes('ondigitalocean.app') ||
      window.location.hostname.includes('services.knls.in'))
  ) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

const getStudentPhone = () => {
  try {
    const raw = localStorage.getItem('student_data');
    const parsed = raw ? JSON.parse(raw) : null;
    return String(parsed?.phone || '').replace(/\D/g, '').slice(-10);
  } catch {
    return '';
  }
};

const getAttachmentUrl = (attachment) => {
  if (!attachment || typeof attachment !== 'object') return '';
  return (
    attachment.fileUrl ||
    attachment.file_url ||
    attachment.imageUrl ||
    attachment.image_url ||
    attachment.pdfUrl ||
    attachment.pdf_url ||
    ''
  );
};

const isWebProtectedDocumentType = (normalizedType) =>
  normalizedType === 'pdf' || normalizedType === 'image';

const looksLikeMediaUrl = (value = '') => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return false;
  return (
    /^https?:\/\//i.test(trimmed) ||
    trimmed.startsWith('/uploads/') ||
    trimmed.startsWith('/api/uploads/')
  );
};

const resolveContentFileUrl = (contentData = {}, trimmed = '') => {
  const nested = contentData?.contentData || contentData?.content_data || {};
  const pdfData = contentData?.pdfData || nested?.pdfData || {};
  const url =
    contentData?.imageUrl ||
    contentData?.image_url ||
    contentData?.pdfUrl ||
    contentData?.pdf_url ||
    contentData?.fileUrl ||
    contentData?.file_url ||
    nested?.imageUrl ||
    nested?.image_url ||
    nested?.pdfUrl ||
    nested?.pdf_url ||
    nested?.fileUrl ||
    nested?.file_url ||
    pdfData?.imageUrl ||
    pdfData?.pdfUrl ||
    pdfData?.fileUrl ||
    (looksLikeMediaUrl(trimmed) ? trimmed : '');
  return String(url || '').trim();
};

const WebDocumentViewer = ({ fileUrl, normalizedType, fileName = 'Course document' }) => {
  if (!fileUrl) {
    return (
      <Alert severity="info">
        No document available yet.
      </Alert>
    );
  }

  return (
    <AttachmentPreviewPanel
      attachment={{
        fileUrl,
        fileName,
        mimeType: normalizedType === 'image' ? 'image/*' : 'application/pdf',
      }}
    />
  );
};

const isImageMime = (mime = '', name = '') => {
  const lower = String(name).toLowerCase();
  return String(mime).startsWith('image/') || /\.(jpg|jpeg|png|gif|webp)$/i.test(lower);
};

const formatTimer = (ms) => {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const compressImageFile = (file, maxWidth = 1600, quality = 0.68) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      let { width, height } = img;
      const scale = Math.min(1, maxWidth / Math.max(width, height, 1));
      width = Math.round(width * scale);
      height = Math.round(height * scale);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        URL.revokeObjectURL(objectUrl);
        reject(new Error('Canvas not supported'));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(objectUrl);
      canvas.toBlob(
        (blob) => {
          if (!blob) {
            reject(new Error('Image compression failed'));
            return;
          }
          const baseName = (file.name || 'photo').replace(/\.[^.]+$/, '');
          resolve(new File([blob], `${baseName}.jpg`, { type: 'image/jpeg' }));
        },
        'image/jpeg',
        quality,
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Could not read image'));
    };
    img.src = objectUrl;
  });

const normalizeServerAttachments = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((item) => {
      const fileUrl = getAttachmentUrl(item);
      if (!fileUrl && !item?.dataUrl) return null;
      return {
        fileName: item.fileName || item.file_name || 'attachment',
        mimeType: item.mimeType || item.mime_type || '',
        fileSize: item.fileSize || item.file_size || null,
        source: item.source || 'file',
        fileUrl,
        dataUrl: item.dataUrl || item.data_url || '',
      };
    })
    .filter(Boolean);
};

const AssignmentAttachmentView = ({ attachment, label }) => {
  const url = getAttachmentUrl(attachment);
  if (!url) return null;
  const mime = attachment?.mimeType || attachment?.mime_type || '';
  const name = attachment?.fileName || attachment?.file_name || label || 'Attachment';

  if (isImageMime(mime, name)) {
    return (
      <Box>
        {label ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {label}
          </Typography>
        ) : null}
        <img
          src={url}
          alt={name}
          style={{ maxWidth: '100%', maxHeight: 320, objectFit: 'contain', borderRadius: 8 }}
        />
      </Box>
    );
  }

  return (
    <Button
      variant="outlined"
      size="small"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      startIcon={<PictureAsPdfOutlinedIcon />}
    >
      {label ? `${label}: ` : ''}
      {name}
    </Button>
  );
};

const AssignmentRenderer = ({ contentId, contentData }) => {
  const isFullscreen = useFullscreenContent();
  const assignment = contentData?.assignment || contentData || {};
  const questions = useMemo(
    () => (Array.isArray(assignment.questions) ? assignment.questions : []),
    [assignment.questions],
  );
  const submissionTypes = assignment.submissionTypes || {
    text: true,
    image: true,
    pdf: true,
    camera: true,
  };
  const timeLimitMinutes = Number(assignment.timeLimitMinutes || 0);

  const [answers, setAnswers] = useState({});
  const [savedAttachments, setSavedAttachments] = useState({});
  const [pendingAttachments, setPendingAttachments] = useState({});
  const [submission, setSubmission] = useState(null);
  const [timerExpiresAt, setTimerExpiresAt] = useState(null);
  const [remainingMs, setRemainingMs] = useState(null);
  const [savingQuestionId, setSavingQuestionId] = useState('');
  const [uploadingQuestionId, setUploadingQuestionId] = useState('');
  const [savingDraft, setSavingDraft] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const saveTimers = useRef({});
  const fileInputRefs = useRef({});
  const studentPhone = getStudentPhone();

  const submitted =
    submission?.status === 'submitted' || submission?.status === 'reviewed';
  const timerExpired = remainingMs !== null && remainingMs <= 0;
  const locked = submitted || timerExpired;
  const showAnswerKey =
    submitted &&
    (assignment.answerKeyText ||
      getAttachmentUrl(assignment.answerKeyAttachment));

  const applySubmission = useCallback((existing) => {
    setSubmission(existing);
    const nextAnswers = {};
    const nextSaved = {};
    (existing?.answers || []).forEach((answer) => {
      const qId = answer.questionId || answer.question_id;
      nextAnswers[qId] = answer.answerText || answer.answer_text || '';
      nextSaved[qId] = normalizeServerAttachments(answer.attachments);
    });
    setAnswers(nextAnswers);
    setSavedAttachments(nextSaved);

    const expires =
      existing?.timer_expires_at ||
      existing?.timerExpiresAt ||
      null;
    if (expires) {
      const expiresMs = new Date(expires).getTime();
      setTimerExpiresAt(expiresMs);
      setRemainingMs(Math.max(0, expiresMs - Date.now()));
    }
  }, []);

  useEffect(() => {
    if (!contentId || !studentPhone) return;
    let active = true;

    const load = async () => {
      try {
        const [startRes, submissionRes] = await Promise.all([
          axios.post(
            `${API_BASE_URL}/lms/assignments/${contentId}/start`,
            { studentPhone },
            { headers: getStudentApiHeaders() },
          ),
          axios.get(`${API_BASE_URL}/lms/assignments/${contentId}/submission`, {
            headers: getStudentApiHeaders(),
            params: { studentPhone },
          }),
        ]);
        if (!active) return;
        const started = startRes.data?.data || null;
        const existing = submissionRes.data?.data || started || null;
        applySubmission(existing);
      } catch (err) {
        if (!active) return;
        try {
          const submissionRes = await axios.get(
            `${API_BASE_URL}/lms/assignments/${contentId}/submission`,
            {
              headers: getStudentApiHeaders(),
              params: { studentPhone },
            },
          );
          if (active) applySubmission(submissionRes.data?.data || null);
        } catch {
          if (active) setError('Could not load assignment. Please refresh and try again.');
        }
        if (active && err?.response?.data?.message) {
          setError(err.response.data.message);
        }
      }
    };

    load();
    return () => {
      active = false;
      Object.values(saveTimers.current).forEach(clearTimeout);
    };
  }, [contentId, studentPhone, applySubmission]);

  useEffect(() => {
    if (!timerExpiresAt || submitted) return undefined;
    const tick = () => {
      setRemainingMs(Math.max(0, timerExpiresAt - Date.now()));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [timerExpiresAt, submitted]);

  const buildAttachmentsPayload = useCallback(
    (questionId) => {
      const pending = pendingAttachments[questionId] || [];
      return pending.map((item) => ({
        fileName: item.fileName,
        mimeType: item.mimeType,
        fileSize: item.fileSize,
        source: item.source,
        dataUrl: item.dataUrl,
      }));
    },
    [pendingAttachments],
  );

  const saveAnswer = useCallback(
    async (question, answerText, options = {}) => {
      if (!studentPhone || locked) return;
      const { clearPending = true } = options;
      setSavingQuestionId(question.id);
      setError('');
      setNotice('');
      try {
        const attachments = buildAttachmentsPayload(question.id);
        const response = await axios.post(
          `${API_BASE_URL}/lms/assignments/${contentId}/answers`,
          {
            studentPhone,
            questionId: question.id,
            questionText: question.questionText,
            answerText,
            maxMarks: Number(question.marks || 0),
            attachments,
          },
          { headers: getStudentApiHeaders() },
        );
        const updated = response.data?.data?.submission || null;
        if (updated) {
          applySubmission(updated);
        }
        if (clearPending && attachments.length) {
          setPendingAttachments((prev) => {
            const next = { ...prev };
            delete next[question.id];
            return next;
          });
        }
      } catch (err) {
        setError(err?.response?.data?.message || 'Autosave failed. Please try again.');
        throw err;
      } finally {
        setSavingQuestionId('');
      }
    },
    [studentPhone, locked, buildAttachmentsPayload, contentId, applySubmission],
  );

  const handleAnswerChange = (question, value) => {
    setAnswers((prev) => ({ ...prev, [question.id]: value }));
    clearTimeout(saveTimers.current[question.id]);
    saveTimers.current[question.id] = setTimeout(() => {
      saveAnswer(question, value);
    }, 800);
  };

  const prepareFileAttachment = async (file, source) => {
    let processed = file;
    if (isImageMime(file.type, file.name)) {
      processed = await compressImageFile(file);
    }
    const dataUrl = await fileToDataUrl(processed);
    return {
      fileName: processed.name || file.name || 'attachment',
      mimeType: processed.type || file.type || 'application/octet-stream',
      fileSize: processed.size || file.size || 0,
      source,
      dataUrl,
    };
  };

  const handleFilePick = async (question, files, source) => {
    if (!files?.length || locked) return;
    setUploadingQuestionId(question.id);
    setError('');
    try {
      const prepared = await Promise.all(
        Array.from(files).map((file) => prepareFileAttachment(file, source)),
      );
      setPendingAttachments((prev) => ({
        ...prev,
        [question.id]: [...(prev[question.id] || []), ...prepared],
      }));
      await saveAnswer(question, answers[question.id] || '', { clearPending: true });
    } catch (err) {
      setError(err?.message || 'Could not upload file. Please try again.');
    } finally {
      setUploadingQuestionId('');
    }
  };

  const triggerFileInput = (questionId, accept, source) => {
    const input = fileInputRefs.current[`${questionId}-${source}`];
    if (input) input.click();
  };

  const handleSubmit = async () => {
    if (locked) return;
    Object.values(saveTimers.current).forEach(clearTimeout);
    setSubmitting(true);
    setError('');
    try {
      for (const question of questions) {
        await saveAnswer(question, answers[question.id] || '');
      }
      const response = await axios.post(
        `${API_BASE_URL}/lms/assignments/${contentId}/submit`,
        { studentPhone },
        { headers: getStudentApiHeaders() },
      );
      applySubmission(response.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not submit assignment.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveDraft = async () => {
    if (locked) return;
    Object.values(saveTimers.current).forEach(clearTimeout);
    setSavingDraft(true);
    setError('');
    setNotice('');
    try {
      for (const question of questions) {
        await saveAnswer(question, answers[question.id] || '');
      }
      setNotice('Draft saved. You can review and edit it before final submission.');
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not save draft.');
    } finally {
      setSavingDraft(false);
    }
  };

  const renderQuestionAttachments = (question) => {
    const saved = savedAttachments[question.id] || [];
    const pending = pendingAttachments[question.id] || [];
    const all = [...saved, ...pending];
    if (!all.length) return null;
    return (
      <Stack spacing={1}>
        {all.map((item, idx) => {
          const previewUrl =
            item.dataUrl ||
            item.fileUrl ||
            (item.mimeType?.startsWith('image/') ? item.dataUrl : '');
          if (previewUrl && isImageMime(item.mimeType, item.fileName)) {
            return (
              <Box key={`${item.fileName}-${idx}`}>
                <img
                  src={previewUrl}
                  alt={item.fileName}
                  style={{ maxWidth: '100%', maxHeight: 240, objectFit: 'contain', borderRadius: 8 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {item.fileName}
                </Typography>
              </Box>
            );
          }
          const link = item.fileUrl || item.dataUrl;
          return link ? (
            <Button
              key={`${item.fileName}-${idx}`}
              variant="text"
              size="small"
              href={link}
              target="_blank"
              rel="noopener noreferrer"
            >
              {item.fileName}
            </Button>
          ) : (
            <Chip key={`${item.fileName}-${idx}`} size="small" label={item.fileName} />
          );
        })}
      </Stack>
    );
  };

  if (!questions.length) {
    return <Alert severity="info">This assignment has no questions yet.</Alert>;
  }

  return (
    <FullscreenFillContainer sx={isFullscreen ? { overflow: 'auto' } : {}}>
    <Stack spacing={2} sx={{ pb: isFullscreen ? 1 : 0 }}>
      {timeLimitMinutes > 0 && remainingMs !== null && !submitted ? (
        <Alert
          severity={timerExpired ? 'error' : remainingMs < 5 * 60 * 1000 ? 'warning' : 'info'}
          icon={<TimerOutlinedIcon />}
        >
          {timerExpired
            ? 'Time is up. Your answers are locked.'
            : `Time remaining: ${formatTimer(remainingMs)}`}
        </Alert>
      ) : null}

      {assignment.instructions ? (
        <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
          {assignment.instructions}
        </Alert>
      ) : null}
      {assignment.instructionAttachment ? (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <AssignmentAttachmentView
            attachment={assignment.instructionAttachment}
            label="Instruction attachment"
          />
        </Paper>
      ) : null}

      {submission?.status ? (
        <Chip
          label={
            submission.status === 'reviewed'
              ? `Reviewed: ${submission.awarded_marks || 0}/${submission.total_marks || assignment.totalMarks || 0}`
              : `Status: ${submission.status}`
          }
          color={
            submission.status === 'reviewed'
              ? 'success'
              : submission.status === 'submitted'
                ? 'primary'
                : 'default'
          }
          sx={{ alignSelf: 'flex-start' }}
        />
      ) : null}

      {submission?.status === 'reviewed' && submission?.remarks ? (
        <Alert severity="info" sx={{ whiteSpace: 'pre-wrap' }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            Admin Remarks
          </Typography>
          {submission.remarks}
        </Alert>
      ) : null}

      {showAnswerKey ? (
        <Alert severity="success" sx={{ whiteSpace: 'pre-wrap' }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            Answer Key
          </Typography>
          {assignment.answerKeyText || null}
          {assignment.answerKeyAttachment ? (
            <Box sx={{ mt: assignment.answerKeyText ? 1 : 0 }}>
              <AssignmentAttachmentView attachment={assignment.answerKeyAttachment} />
            </Box>
          ) : null}
        </Alert>
      ) : null}

      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success">{notice}</Alert>}

      {questions.map((question, index) => (
        <Card variant="outlined" key={question.id}>
          <CardContent>
            <Stack spacing={1.5}>
              <Stack direction="row" justifyContent="space-between" spacing={2}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {index + 1}. {question.questionText || 'See attached question'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {question.marks || 0} marks
                </Typography>
              </Stack>

              {question.attachment ? (
                <AssignmentAttachmentView
                  attachment={question.attachment}
                  label="Question attachment"
                />
              ) : null}

              {submissionTypes.text !== false ? (
                <TextField
                  multiline
                  minRows={4}
                  fullWidth
                  disabled={locked || submitting || savingDraft}
                  value={answers[question.id] || ''}
                  onChange={(e) => handleAnswerChange(question, e.target.value)}
                  placeholder="Type your answer here. It saves automatically."
                />
              ) : null}

              {renderQuestionAttachments(question)}

              {!locked ? (
                <Stack direction="row" flexWrap="wrap" gap={1}>
                  {submissionTypes.image !== false ? (
                    <>
                      <input
                        ref={(el) => {
                          fileInputRefs.current[`${question.id}-image`] = el;
                        }}
                        type="file"
                        accept="image/*"
                        multiple
                        hidden
                        onChange={(e) => {
                          handleFilePick(question, e.target.files, 'image');
                          e.target.value = '';
                        }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ImageOutlinedIcon />}
                        disabled={submitting || savingDraft || uploadingQuestionId === question.id}
                        onClick={() => triggerFileInput(question.id, 'image/*', 'image')}
                      >
                        Upload image
                      </Button>
                    </>
                  ) : null}
                  {submissionTypes.camera !== false ? (
                    <>
                      <input
                        ref={(el) => {
                          fileInputRefs.current[`${question.id}-camera`] = el;
                        }}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        hidden
                        onChange={(e) => {
                          handleFilePick(question, e.target.files, 'camera');
                          e.target.value = '';
                        }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PhotoCameraOutlinedIcon />}
                        disabled={submitting || savingDraft || uploadingQuestionId === question.id}
                        onClick={() => triggerFileInput(question.id, 'image/*', 'camera')}
                      >
                        Camera
                      </Button>
                    </>
                  ) : null}
                  {submissionTypes.pdf !== false ? (
                    <>
                      <input
                        ref={(el) => {
                          fileInputRefs.current[`${question.id}-pdf`] = el;
                        }}
                        type="file"
                        accept="application/pdf,.pdf"
                        multiple
                        hidden
                        onChange={(e) => {
                          handleFilePick(question, e.target.files, 'pdf');
                          e.target.value = '';
                        }}
                      />
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PictureAsPdfOutlinedIcon />}
                        disabled={submitting || savingDraft || uploadingQuestionId === question.id}
                        onClick={() => triggerFileInput(question.id, 'application/pdf', 'pdf')}
                      >
                        Upload PDF
                      </Button>
                    </>
                  ) : null}
                </Stack>
              ) : null}

              <Typography variant="caption" color="text.secondary">
                {locked
                  ? timerExpired && !submitted
                    ? 'Time expired — editing is locked.'
                    : 'This assignment is locked after submission.'
                  : uploadingQuestionId === question.id
                    ? 'Uploading...'
                    : savingQuestionId === question.id
                      ? 'Saving...'
                      : 'Draft autosaves after you stop typing or upload a file.'}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      ))}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        <Button
          variant="outlined"
          onClick={handleSaveDraft}
          disabled={locked || submitting || savingDraft || !studentPhone}
          sx={{ flex: 1 }}
        >
          {savingDraft ? 'Saving Draft...' : 'Save Draft'}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={locked || submitting || savingDraft || !studentPhone}
          sx={{ flex: 1 }}
        >
          {submitted ? 'Submitted' : submitting ? 'Submitting...' : 'Submit Assignment'}
        </Button>
      </Stack>
      {!studentPhone && (
        <Alert severity="warning">Student session is missing. Please login again.</Alert>
      )}
    </Stack>
    </FullscreenFillContainer>
  );
};

const ContentRenderer = ({
  content,
  type,
  contentId,
  contentData,
  chapterId,
  chapterTitle,
  contentTitle,
}) => {
  const isFullscreen = useFullscreenContent();
  const normalizedType = String(type || contentData?.type || contentData?.viewerType || '').toLowerCase();
  const trimmed = (content || '').trim();
  const studentPhone = getStudentPhone();
  const allowWebDocuments = hasWebDocumentAccess(studentPhone);
  const isProtectedDocumentType = isWebProtectedDocumentType(normalizedType) && !allowWebDocuments;
  const contentFileUrl = resolveContentFileUrl(contentData, trimmed);
  const sourcePdfUrl = contentFileUrl;
  const contentFileName = contentData?.fileName || contentData?.file_name || 'Course document';

  if (normalizedType === 'assignment' || contentData?.assignment) {
    return <AssignmentRenderer contentId={contentId} contentData={contentData} />;
  }

  if (normalizedType === 'coding' || contentData?.coding || contentData?.codingData) {
    return (
      <CodingChallengeLauncher
        contentId={contentId}
        contentData={contentData}
        chapterTitle={chapterTitle}
        contentTitle={contentTitle}
      />
    );
  }

  if (
    normalizedType === 'audio_source' ||
    normalizedType === 'audio_loop' ||
    normalizedType === 'verse_loop'
  ) {
    const audioUrl = contentData?.audioUrl || contentData?.originalUrl || '';
    const fullAudioUrl =
      contentData?.fullAudioUrl ||
      contentData?.full_audio_url ||
      contentData?.originalUrl ||
      audioUrl ||
      '';
    const isVerse = normalizedType !== 'audio_source';
    const startSec = Number(contentData?.startSec ?? contentData?.start_sec) || 0;
    const endSec = Number(contentData?.endSec ?? contentData?.end_sec) || 0;
    const defaultStartSec = Number(
      contentData?.defaultStartSec ?? contentData?.default_start_sec ?? startSec,
    ) || 0;
    const defaultEndSec = Number(
      contentData?.defaultEndSec ?? contentData?.default_end_sec ?? endSec,
    ) || 0;
    const resolvedChapterId = Number(
      chapterId ?? contentData?.chapterId ?? contentData?.chapter_id,
    ) || 0;
    return (
      <VerseLoopPlayer
        audioUrl={audioUrl}
        originalUrl={fullAudioUrl || contentData?.originalUrl || audioUrl}
        fullAudioUrl={fullAudioUrl}
        startSec={startSec}
        endSec={endSec}
        defaultStartSec={defaultStartSec}
        defaultEndSec={defaultEndSec}
        defaultVerseText={
          contentData?.defaultVerseText ||
          contentData?.default_verse_text ||
          contentData?.verseText ||
          contentData?.verse_text ||
          ''
        }
        defaultTextSpeed={
          Number(
            contentData?.defaultTextSpeed ??
              contentData?.default_text_speed ??
              contentData?.textSpeed ??
              contentData?.text_speed,
          ) || 1
        }
        portionOverride={Boolean(
          contentData?.portionOverride ?? contentData?.portion_override,
        )}
        durationSec={Number(contentData?.durationSec ?? contentData?.duration_sec) || 0}
        loopCount={Number(contentData?.loopCount ?? contentData?.loop_count) || (isVerse ? 5 : 1)}
        contentId={contentId}
        chapterId={resolvedChapterId}
        title={contentTitle || ''}
        verseText={contentData?.verseText || contentData?.verse_text || ''}
        textSpeed={contentData?.textSpeed ?? contentData?.text_speed}
        verse={isVerse && endSec > startSec}
        allowStudentPortionEdit={isVerse}
      />
    );
  }

  if (normalizedType === 'link') {
    const url = (contentData?.url || contentData?.link || trimmed || '').trim();
    const description = (contentData?.description || '').trim();

    if (!url) {
      return (
        <Alert severity="info">
          No website link available yet.
        </Alert>
      );
    }

    return (
      <Stack spacing={2}>
        {description ? (
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
            {description}
          </Typography>
        ) : null}
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Website
          </Typography>
          <Typography
            variant="body2"
            sx={{ wordBreak: 'break-all', mb: 2 }}
          >
            {url}
          </Typography>
          <Button
            variant="contained"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewIcon />}
          >
            Open Website
          </Button>
        </Paper>
      </Stack>
    );
  }

  if (isProtectedDocumentType) {
    return <AndroidOnlyDocumentNotice />;
  }

  if (!trimmed) {
    if (normalizedType === 'reader' || sourcePdfUrl) {
      return (
        <Stack spacing={2}>
          {sourcePdfUrl ? (
            allowWebDocuments ? (
              <WebDocumentViewer
                fileUrl={sourcePdfUrl}
                normalizedType="pdf"
                fileName={contentFileName}
              />
            ) : (
              <AndroidOnlyDocumentNotice />
            )
          ) : null}
          <Alert severity="info">
            No notes content yet for this item.
          </Alert>
        </Stack>
      );
    }

    return (
      <Alert severity="info">
        No content available yet
      </Alert>
    );
  }

  switch (normalizedType) {
    case 'video': {
      const youtubeMatch = trimmed.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
      if (youtubeMatch) {
        const videoId = youtubeMatch[1];
        if (isFullscreen) {
          return (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: '#000',
                borderRadius: { xs: 1, sm: 2 },
                overflow: 'hidden',
              }}
            >
              <Box
                component="iframe"
                src={`https://www.youtube.com/embed/${videoId}`}
                title="Video content"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                sx={{
                  flex: 1,
                  width: '100%',
                  height: '100%',
                  minHeight: 0,
                  border: 0,
                }}
              />
            </Box>
          );
        }
        return (
          <Box
            sx={{
              position: 'relative',
              paddingTop: '56.25%',
              bgcolor: '#000',
              borderRadius: 1,
              overflow: 'hidden',
              minHeight: { xs: 220, md: 360 },
            }}
          >
            <iframe
              src={`https://www.youtube.com/embed/${videoId}`}
              title="Video content"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
            />
          </Box>
        );
      }

      return (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Video link:
          </Typography>
          <a href={trimmed} target="_blank" rel="noopener noreferrer">
            {trimmed}
          </a>
        </Box>
      );
    }

    case 'reader':
      return (
        <FullscreenFillContainer
          sx={isFullscreen ? { overflow: 'auto' } : {}}
        >
          <Stack spacing={2} sx={{ height: isFullscreen ? '100%' : 'auto' }}>
            {sourcePdfUrl ? (
              allowWebDocuments ? (
                <WebDocumentViewer
                  fileUrl={sourcePdfUrl}
                  normalizedType="pdf"
                  fileName={contentFileName}
                />
              ) : (
                <PdfAppOnlyAlert />
              )
            ) : null}
            <Box sx={{ flex: isFullscreen ? 1 : undefined, minHeight: isFullscreen ? 0 : undefined, overflow: isFullscreen ? 'auto' : undefined }}>
              <KindleReaderContent content={trimmed} contentId={contentId} />
            </Box>
          </Stack>
        </FullscreenFillContainer>
      );

    case 'pdf':
      return allowWebDocuments ? (
        <WebDocumentViewer
          fileUrl={contentFileUrl}
          normalizedType="pdf"
          fileName={contentFileName}
        />
      ) : (
        <AndroidOnlyDocumentNotice />
      );

    case 'image':
      return allowWebDocuments ? (
        <WebDocumentViewer
          fileUrl={contentFileUrl}
          normalizedType="image"
          fileName={contentFileName}
        />
      ) : (
        <AndroidOnlyDocumentNotice />
      );

    case 'text':
    default:
      return (
        <FullscreenFillContainer sx={isFullscreen ? { overflow: 'auto' } : {}}>
          <Box sx={{ flex: isFullscreen ? 1 : undefined, overflow: isFullscreen ? 'auto' : undefined }}>
            <Typography
              variant="body1"
              sx={{
                whiteSpace: 'pre-wrap',
                lineHeight: 1.8,
                fontSize: isFullscreen ? { xs: '1rem', md: '1.1rem' } : '1.05rem',
              }}
            >
              {trimmed}
            </Typography>
          </Box>
        </FullscreenFillContainer>
      );
  }
};

export default ContentRenderer;
